'use client';

import dynamic from 'next/dynamic';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import OptimizedImage from '../ui/OptimizedImage';
import { getCoverImageForContext } from '../../../utils/coverImageUtils';
import {
  pickCoverTitleFromSources,
  pickCoverYearFromSources,
} from '../../../lib/destinationTemplateUtils';
import { fitTitleFontSizePx } from '../../../utils/coverTitleFit';
import { coverDiscountPercent } from '../../../lib/coverDiscount';
import { COVER_NEW_BADGE_LABEL, isCoverNew } from '../../../lib/coverNewBadge';
import { coverLabel } from '../../../lib/coverLabel';
import styles from '../../../styles/components/cover/coverOverlay.module.scss';

const CoverFavoriteHeart = dynamic(() => import('./CoverFavoriteHeart'), {
  ssr: false,
  loading: () => null,
});

// Тиктака и се рендерира на сървъра (за разлика от сърцето) — баджът е част от
// офертата и трябва да е в HTML-а, който вижда и Google.
const CoverDiscountBadge = dynamic(() => import('./CoverDiscountBadge'));

const DEFAULT_TITLE_Y_PERCENT = 8;
const DEFAULT_YEAR_Y_PERCENT = 18;
const DEFAULT_TITLE_SIZE = 82;
const DEFAULT_YEAR_SIZE = 24;
const REFERENCE_COVER_WIDTH = 400;
// „Събиране" на заглавието до 1 ред (като в едитора), без DOM измерване → SSR-safe, без мигане.
const TITLE_FIT_WIDTH_RATIO = 0.86; // събиране до малко под рамката (90%) → гарантиран 1 ред
const LONG_TITLE_CHARS = 16; // по-дълго заглавие → по подразбиране ляво подравнено
// Едиторът пресмята размерите в px спрямо своя canvas (front cover ≈ 2362px при 300dpi),
// а template-ите са в база-400. Привеждаме canvas-размерите към база-400, за да не излизат
// в пъти по-големи (= CANVAS_FRONT_COVER_WIDTH_300DPI в CoverThumbnail.jsx).
const CANVAS_FRONT_COVER_WIDTH = 2362;
const CANVAS_PX_TO_BASE = REFERENCE_COVER_WIDTH / CANVAS_FRONT_COVER_WIDTH;
const DEFAULT_SM_SCALE_NUMERATOR = 80;
const DEFAULT_MD_SCALE_NUMERATOR = 332;
// Ниски минимуми → fitTitleFontSizePx винаги може да свие дълго заглавие на 1 ред (без рязане).
const MIN_TITLE_PX_SM = 3;
// По-нисък минимум за годината → много дългата година ("2026"×N) може да се свие достатъчно да се
// събере на 1 ред в малките превюта (преди 5/10 px беше твърде голямо и преливаше).
const MIN_YEAR_PX_SM = 1;
const MIN_TITLE_PX_MD = 5;
const MIN_YEAR_PX_MD = 2;
const SM_IMAGE_WIDTH = 80;
const SM_IMAGE_HEIGHT = 100;
const MD_IMAGE_WIDTH = 200;
const MD_IMAGE_HEIGHT = 150;

function toPercentNum(val, fallback) {
  const n = val != null ? Number(val) : NaN;
  if (Number.isFinite(n) && n >= 0 && n <= 100) {
    return n;
  }
  return fallback;
}

function toSizeNum(val, fallback) {
  const n = val != null ? Number(val) : NaN;
  if (Number.isFinite(n) && n >= 0) {
    return n;
  }
  return fallback;
}

function getYearTransform(yearTextAlign) {
  if (yearTextAlign === 'left') {
    return 'translate(0, 0)';
  }
  if (yearTextAlign === 'right') {
    return 'translate(-100%, 0)';
  }
  return 'translate(-50%, 0)';
}

/**
 * CoverOverlay - Unified cover image + title/year overlay.
 * Proportional sizing based on admin panel settings.
 * Uses getCoverImageForContext(template, size) for image resolution.
 *
 * @param {Object}  template        - Template with admin fields
 * @param {string}  destinationName - Fallback title text
 * @param {Object}  texts           - User-override { coverTitle, coverYear }
 * @param {Object}  categoryData    - Category fallback (font, colors, bg)
 * @param {'sm'|'md'|'lg'} size     - Image size variant
 * @param {string}  className       - Extra CSS class
 * @param {string}  imageLoading    - 'lazy' | 'eager'
 * @param {boolean} priority        - Next.js Image priority
 * @param {boolean} textureOverlay  - Show book texture (homepage large)
 * @param {boolean} unoptimized     - Pass through to Image
 * @param {string}  placeholderChar - Fallback char when no template/image
 * @param {number}  titleSizePx     - Override: use this px size directly (e.g. from CoverThumbnail)
 * @param {number}  yearSizePx     - Override: use this px size directly
 * @param {string}  favoriteSlug - Дестинация slug (любими). Същият механизъм за travogue/babyvogue.
 * @param {number}  favoriteTemplateId - СТАБИЛЕН templateId на корицата (не позиция)
 * @param {boolean} showFavoriteButton - Показва сърце при валидни slug+templateId. Дефолт: false.
 * @param {boolean} showCatalogBadges - Разрешава каталожните баджове: „NEW" (горе вляво) и
 *   намалението с таймер (горе вдясно), всеки според настройките си в админа. Нарочно е
 *   opt-in и общ за двата: същият компонент рисува корицата и в едитора, в количката, в
 *   чекаута и в поръчките, а там те нямат смисъл — това е книгата на клиента, не оферта
 *   от каталога.
 */
const CoverOverlay = memo(
  ({
    template = null,
    destinationName,
    texts = null,
    categoryData = null,
    size = 'sm',
    className,
    imageLoading = 'lazy',
    priority = false,
    textureOverlay = false,
    unoptimized,
    placeholderChar,
    favoriteSlug = null,
    favoriteTemplateId = null,
    showFavoriteButton = false,
    showCatalogBadges = false,
  }) => {
    const isSmall = size === 'sm';
    const defaultScaleNum = isSmall ? DEFAULT_SM_SCALE_NUMERATOR : DEFAULT_MD_SCALE_NUMERATOR;

    const wrapRef = useRef(null);
    const [scale, setScale] = useState(defaultScaleNum / REFERENCE_COVER_WIDTH);

    useEffect(() => {
      const el = wrapRef.current;
      if (!el) {
        return () => {};
      }
      const update = () => {
        const w = el.offsetWidth;
        if (w && Number.isFinite(w)) {
          setScale(w / REFERENCE_COVER_WIDTH);
        }
      };
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    const fallbackTemplate = categoryData?.details?.templates?.[0];
    const tpl = template || fallbackTemplate;

    // User-dragged positions from saved book (texts.coverTitlePosition / coverYearPosition)
    const titlePos = texts?.coverTitlePosition;
    const yearPos = texts?.coverYearPosition;
    const titleX = toPercentNum(
      titlePos?.x ?? tpl?.coverTitleX ?? fallbackTemplate?.coverTitleX,
      50,
    );
    const titleY = toPercentNum(
      titlePos?.y ?? tpl?.coverTitleY ?? fallbackTemplate?.coverTitleY,
      DEFAULT_TITLE_Y_PERCENT,
    );
    const yearX = toPercentNum(yearPos?.x ?? tpl?.coverYearX ?? fallbackTemplate?.coverYearX, 50);
    const yearY = toPercentNum(
      yearPos?.y ?? tpl?.coverYearY ?? fallbackTemplate?.coverYearY,
      DEFAULT_YEAR_Y_PERCENT,
    );
    // Само РЪЧНИЯ размер (потребителят е пипнал степъра) се пази; в canvas px → база-400.
    // Авто-размерът се ПРЕИЗЧИСЛЯВА тук по реалния текст (виж по-долу) — не зависи от запазена
    // calculatedCoverTitleFontSize, затова е винаги точен (поръчки, запазени книги, pagination).
    const manualTitleCanvas = texts?.coverTitleStyle?.fontSize;
    const manualTitleBase =
      manualTitleCanvas != null ? manualTitleCanvas * CANVAS_PX_TO_BASE : null;
    const titleSizeBase = toSizeNum(
      manualTitleBase ?? tpl?.coverTitleSize ?? fallbackTemplate?.coverTitleSize,
      DEFAULT_TITLE_SIZE,
    );
    const manualYearCanvas = texts?.coverYearStyle?.fontSize;
    const manualYearBase = manualYearCanvas != null ? manualYearCanvas * CANVAS_PX_TO_BASE : null;
    // Годината = база (24) × scale, т.е. 24/400 = 6% от ширината на корицата — същата пропорция
    // като в едитора (24:82 спрямо заглавието). Просто и пропорционално, без „умни" изчисления.
    const yearSizeBase = toSizeNum(
      manualYearBase ?? tpl?.coverYearSize ?? fallbackTemplate?.coverYearSize,
      DEFAULT_YEAR_SIZE,
    );

    // User-applied styles from saved book texts
    const titleStyle = texts?.coverTitleStyle || {};
    const yearStyle = texts?.coverYearStyle || {};

    // Същият източник като едитора (template.fontFamily) → мярката съвпада с рендера.
    // ВАЖНО: при втора навигация tpl/fallbackTemplate/categoryData може да липсват → пада на
    // 'Arial' и шрифтът изчезва. Затова ползваме запазения от едитора texts.coverBaseFontFamily
    // като приоритетен fallback (същото като serializer-а) → шрифтът се хваща ВИНАГИ.
    const savedBaseFontFamily = texts?.coverBaseFontFamily;
    const baseTitleFontFamily =
      tpl?.fontFamily ||
      fallbackTemplate?.fontFamily ||
      categoryData?.fontFamily ||
      savedBaseFontFamily ||
      'Arial, sans-serif';
    const titleFontFamily =
      (titleStyle.fontFamily && titleStyle.fontFamily !== 'inherit'
        ? titleStyle.fontFamily
        : null) || baseTitleFontFamily;
    const yearFontFamily =
      (yearStyle.fontFamily && yearStyle.fontFamily !== 'inherit' ? yearStyle.fontFamily : null) ||
      tpl?.yearFontFamily ||
      tpl?.fontFamily ||
      fallbackTemplate?.yearFontFamily ||
      fallbackTemplate?.fontFamily ||
      categoryData?.fontFamily ||
      savedBaseFontFamily ||
      titleFontFamily;
    const yearTextAlign = tpl?.yearTextAlign || fallbackTemplate?.yearTextAlign || 'center';
    const titleLineHeight =
      tpl?.coverTitleLineHeight ?? fallbackTemplate?.coverTitleLineHeight ?? 1.15;
    // Изрично подравняване (потребител > шаблон); default-ът зависи от дължината (виж по-долу).
    const titleAlignExplicit =
      titleStyle.textAlign || tpl?.coverTitleAlign || fallbackTemplate?.coverTitleAlign;

    const backgroundColor =
      tpl?.backgroundColor ??
      tpl?.coverBackground ??
      fallbackTemplate?.backgroundColor ??
      fallbackTemplate?.coverBackground ??
      categoryData?.coverBackground ??
      '#FFF8DC';
    const titleColor =
      titleStyle.color ||
      (tpl?.coverTextColor ??
        tpl?.textColor ??
        fallbackTemplate?.coverTextColor ??
        fallbackTemplate?.textColor ??
        categoryData?.coverTextColor ??
        categoryData?.textColor) ||
      '#1a1a1a';
    const yearColor =
      yearStyle.color ||
      (tpl?.coverYearColor ??
        tpl?.yearColor ??
        fallbackTemplate?.coverYearColor ??
        fallbackTemplate?.yearColor ??
        categoryData?.coverYearColor ??
        categoryData?.yearColor) ||
      '#333333';
    const objectFit = tpl?.objectFit ?? fallbackTemplate?.objectFit ?? 'contain';

    // Година от шаблона; непразен texts.coverYear презаписва. Празен texts.coverYear не крие
    // всички корици (canvas често праща '').
    const coverTexts = useMemo(() => {
      const destName = destinationName?.toUpperCase() || 'DESTINATION';

      // Заглавие — същата логика като годината: празно от темплейта (`''`) = без заглавие;
      // `null` (не зададено) → име на дестинацията.
      const titleFromTemplate = pickCoverTitleFromSources(
        tpl?.coverTitle,
        categoryData?.texts?.coverTitle,
      );
      const hasTitleKey =
        texts != null && Object.prototype.hasOwnProperty.call(texts, 'coverTitle');
      const userTitleTrim =
        hasTitleKey && typeof texts.coverTitle === 'string' ? texts.coverTitle.trim() : '';
      const resolvedTitle = userTitleTrim ? userTitleTrim : titleFromTemplate;
      const title = resolvedTitle === null ? destName : resolvedTitle;

      // Само този template + category.texts — НЕ templates[0], иначе празна година на
      // първата корица „заразява“ всички с null в БД.
      const fromTemplate = pickCoverYearFromSources(tpl?.coverYear, categoryData?.texts?.coverYear);

      const hasYearKey = texts != null && Object.prototype.hasOwnProperty.call(texts, 'coverYear');
      const userTrim =
        hasYearKey && typeof texts.coverYear === 'string' ? texts.coverYear.trim() : '';

      const year = userTrim ? userTrim : fromTemplate;

      return { coverTitle: title, coverYear: year };
    }, [destinationName, tpl?.coverTitle, tpl?.coverYear, texts, categoryData?.texts]);

    // Описателен alt за Google Images от вече сметнатите coverTexts (tri-state се
    // уважава: скрито заглавие/година не влизат). Guard срещу „FRANCE – France".
    const altTitle = (coverTexts.coverTitle || '').trim();
    const altYear = (coverTexts.coverYear || '').trim();
    const altDest =
      destinationName && altTitle.toLowerCase() !== destinationName.trim().toLowerCase()
        ? ` – ${destinationName}`
        : '';
    const imageAlt = altTitle
      ? `Фотокнига „${altTitle}"${altYear ? ` ${altYear}` : ''}${altDest}`
      : `Корица на фотокнига${destinationName ? ` – ${destinationName}` : ''}${altYear ? ` ${altYear}` : ''}`;

    const minTitlePx = isSmall ? MIN_TITLE_PX_SM : MIN_TITLE_PX_MD;
    const minYearPx = isSmall ? MIN_YEAR_PX_SM : MIN_YEAR_PX_MD;
    const coverWidthPx = scale * REFERENCE_COVER_WIDTH; // измерената ширина на корицата (px)
    const yearSizePx = Math.max(minYearPx, yearSizeBase * scale);

    // Желан (дизайн) размер на заглавието, пропорционален на тази корица.
    const designTitlePx = Math.max(minTitlePx, titleSizeBase * scale);
    const isManualTitle = manualTitleBase != null;

    const titleStr = coverTexts.coverTitle || '';
    // Само дълго заглавие на ЕДИН ред → ляво; изричен \n (два реда) → центрирано (като админа).
    const titleIsLong = !titleStr.includes('\n') && titleStr.trim().length > LONG_TITLE_CHARS;
    const titleAlign = titleAlignExplicit || (titleIsLong ? 'left' : 'center');

    // Авто-събиране като едитора: измерваме реалния текст и го свиваме до 86% от ширината → 1 ред.
    // Ръчният размер се уважава (без свиване). Не зависи от запазена стойност → винаги точно.
    const [fittedTitlePx, setFittedTitlePx] = useState(designTitlePx);
    useLayoutEffect(() => {
      if (isManualTitle) {
        setFittedTitlePx(designTitlePx);
        return undefined;
      }
      let cancelled = false;
      const measure = () =>
        !cancelled &&
        setFittedTitlePx(
          fitTitleFontSizePx({
            text: titleStr,
            fontFamily: titleFontFamily,
            fontWeight: titleStyle.fontWeight,
            maxPx: designTitlePx,
            minPx: minTitlePx,
            availableWidthPx: coverWidthPx * TITLE_FIT_WIDTH_RATIO,
          }),
        );
      measure();
      // Шрифтът може да се зареди след първата мярка → премери пак (без мигане към грешен размер).
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        document.fonts.ready.then(measure);
      }
      return () => {
        cancelled = true;
      };
    }, [
      isManualTitle,
      titleStr,
      titleFontFamily,
      titleStyle.fontWeight,
      designTitlePx,
      coverWidthPx,
      minTitlePx,
    ]);

    const effectiveTitlePx = isManualTitle ? designTitlePx : fittedTitlePx;

    // Авто-събиране на годината (както заглавието): дългата година ВИНАГИ се свива да се събере на
    // 1 ред. fit-ът пази размера (maxPx = ръчен/дизайн), но смалява САМО ако текстът не се събира —
    // затова дългата година ("2026"×N) не излиза от край до край в превютата, както в едитора.
    const yearStr = coverTexts.coverYear || '';
    const [fittedYearPx, setFittedYearPx] = useState(yearSizePx);
    useLayoutEffect(() => {
      let cancelled = false;
      const measure = () =>
        !cancelled &&
        setFittedYearPx(
          fitTitleFontSizePx({
            text: yearStr,
            fontFamily: yearFontFamily,
            fontWeight: yearStyle.fontWeight ?? '700',
            maxPx: yearSizePx,
            minPx: minYearPx,
            availableWidthPx: coverWidthPx * TITLE_FIT_WIDTH_RATIO,
          }),
        );
      measure();
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        document.fonts.ready.then(measure);
      }
      return () => {
        cancelled = true;
      };
    }, [yearStr, yearFontFamily, yearStyle.fontWeight, yearSizePx, coverWidthPx, minYearPx]);

    const effectiveYearPx = fittedYearPx;

    const coverStyle = useMemo(
      () => ({
        backgroundColor,
        position: 'relative',
        '--title-x': `${titleX}%`,
        '--title-y': `${titleY}%`,
        '--year-x': `${yearX}%`,
        '--year-y': `${yearY}%`,
        '--title-color': titleColor,
        '--year-color': yearColor,
        '--font-family': titleFontFamily,
        '--year-font-family': yearFontFamily,
        '--title-font-weight': titleStyle.fontWeight ?? '700',
        '--object-fit': objectFit,
        '--title-size': `${effectiveTitlePx}px`,
        '--year-size': `${effectiveYearPx}px`,
        '--year-transform': getYearTransform(yearTextAlign),
        '--title-line-height': titleLineHeight,
        '--title-text-align': titleAlign,
        // Изричен \n (две заглавни линии от админа) → pre, за да се покажат двата реда (размерът
        // вече е сметнат по най-дългия ред). Иначе: ръчен → normal (wrap), авто → 1 ред (свит).
        '--title-white-space': titleStr.includes('\n')
          ? 'pre'
          : isManualTitle
            ? 'normal'
            : 'nowrap',
      }),
      [
        backgroundColor,
        titleX,
        titleY,
        yearX,
        yearY,
        titleColor,
        yearColor,
        titleFontFamily,
        yearFontFamily,
        titleStyle.fontWeight,
        objectFit,
        effectiveTitlePx,
        effectiveYearPx,
        yearTextAlign,
        titleLineHeight,
        titleAlign,
        isManualTitle,
        titleStr,
      ],
    );

    const imagePath = categoryData?.details?.imagePath;
    const imageSrc = useMemo(
      () =>
        getCoverImageForContext(tpl, size, imagePath) ||
        getCoverImageForContext(fallbackTemplate, size, imagePath) ||
        imagePath ||
        '',
      [tpl, fallbackTemplate, size, imagePath],
    );

    const favoriteThumbUrl = useMemo(() => {
      if (!showFavoriteButton) {
        return '';
      }
      return getCoverImageForContext(tpl, 'md', imagePath) || imageSrc || '';
    }, [showFavoriteButton, tpl, imagePath, imageSrc]);

    const showFavoriteHeart =
      showFavoriteButton &&
      typeof favoriteSlug === 'string' &&
      favoriteSlug.trim() !== '' &&
      typeof favoriteTemplateId === 'number' &&
      Number.isInteger(favoriteTemplateId) &&
      favoriteTemplateId >= 0;

    const favoriteLabelSnapshot = useMemo(() => {
      if (!showFavoriteHeart) {
        return '';
      }
      // 2-ри арг (позиция) е само за fallback „корица N“ при липса на seoH1; 0 е достатъчно.
      return coverLabel(tpl, 0, destinationName?.trim() || favoriteSlug.trim());
    }, [showFavoriteHeart, tpl, destinationName, favoriteSlug]);

    // Периодът се мери на дни, затова стойността е една и съща на сървъра и при
    // hydration — виж коментара в lib/coverNewBadge.js.
    const showNew = showCatalogBadges && isCoverNew(tpl);
    // Тук проверяваме само ДАЛИ да рисуваме баджа; самият процент и таймерът се
    // преизчисляват вътре в него на всяка секунда.
    const showDiscount = showCatalogBadges && coverDiscountPercent(tpl) > 0;

    const imgW = isSmall ? SM_IMAGE_WIDTH : MD_IMAGE_WIDTH;
    const imgH = isSmall ? SM_IMAGE_HEIGHT : MD_IMAGE_HEIGHT;
    const imgSizes = isSmall ? '80px' : '400px';

    const sizeClass = size === 'lg' ? styles.sizeLg : size === 'md' ? styles.sizeMd : '';

    const wrapClass = [styles.coverOverlay, sizeClass, className].filter(Boolean).join(' ');

    const hasImage = imageSrc && imageSrc.trim() !== '';
    if (!tpl && !hasImage && placeholderChar) {
      return (
        <div ref={wrapRef} className={wrapClass} style={coverStyle}>
          <div className={styles.placeholderLetter}>{placeholderChar}</div>
        </div>
      );
    }

    return (
      <div ref={wrapRef} className={wrapClass} style={coverStyle}>
        {textureOverlay && <div className={styles.textureOverlay} />}

        {showNew ? (
          <span className={styles.newBadge} aria-label="Нова корица">
            {COVER_NEW_BADGE_LABEL}
          </span>
        ) : null}

        {showDiscount ? <CoverDiscountBadge template={tpl} /> : null}

        {showFavoriteHeart ? (
          <CoverFavoriteHeart
            destinationSlug={favoriteSlug.trim()}
            templateId={favoriteTemplateId}
            labelSnapshot={favoriteLabelSnapshot}
            thumbnailUrl={favoriteThumbUrl}
          />
        ) : null}

        {coverTexts.coverTitle ? (
          <div className={styles.titleContainer}>
            <div className={styles.title}>{coverTexts.coverTitle}</div>
          </div>
        ) : null}

        {coverTexts.coverYear ? <div className={styles.year}>{coverTexts.coverYear}</div> : null}

        <div className={styles.imageContainer}>
          {hasImage ? (
            <OptimizedImage
              src={imageSrc}
              alt={imageAlt}
              width={imgW}
              height={imgH}
              className={styles.image}
              sizes={imgSizes}
              priority={priority}
              loading={priority ? undefined : imageLoading}
              defer={isSmall && !priority}
              unoptimized={unoptimized ?? imageSrc?.startsWith?.('http')}
            />
          ) : (
            <div className={styles.placeholder} />
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.destinationName === nextProps.destinationName &&
    prevProps.size === nextProps.size &&
    prevProps.template?.templateId === nextProps.template?.templateId &&
    prevProps.template?.thumbnailSm === nextProps.template?.thumbnailSm &&
    prevProps.template?.thumbnailMd === nextProps.template?.thumbnailMd &&
    prevProps.template?.thumbnailLg === nextProps.template?.thumbnailLg &&
    prevProps.template?.image === nextProps.template?.image &&
    prevProps.texts === nextProps.texts &&
    prevProps.categoryData === nextProps.categoryData &&
    prevProps.favoriteSlug === nextProps.favoriteSlug &&
    prevProps.favoriteTemplateId === nextProps.favoriteTemplateId &&
    prevProps.showFavoriteButton === nextProps.showFavoriteButton &&
    prevProps.showCatalogBadges === nextProps.showCatalogBadges &&
    prevProps.template?.isNew === nextProps.template?.isNew &&
    prevProps.template?.newFrom === nextProps.template?.newFrom &&
    prevProps.template?.newUntil === nextProps.template?.newUntil &&
    prevProps.template?.isDiscounted === nextProps.template?.isDiscounted &&
    prevProps.template?.discountPercent === nextProps.template?.discountPercent &&
    prevProps.template?.discountFrom === nextProps.template?.discountFrom &&
    prevProps.template?.discountUntil === nextProps.template?.discountUntil,
);

CoverOverlay.displayName = 'CoverOverlay';

export default CoverOverlay;
